import {
  Users,
  UserCheck,
  BookOpen,
  TrendingUp,
  DollarSign,
  Video,
  Mail,
  MessageCircle,
  Award,
  Star,
  CheckCircle,
  Clock,
} from "lucide-react"
import StatCard from "./StatCard"
import BarChart from "./BarChart"
import LineChart from "./LineChart"
import DonutChart from "./DonutChart"
import TopList from "./TopList"

const formatNumber = (v) => (v ?? 0).toLocaleString("fr-FR")
const formatMoney = (v, currency = "XOF") =>
  `${(v ?? 0).toLocaleString("fr-FR")} ${currency === "XOF" ? "FCFA" : currency}`

export default function DashboardStats({
  overview,
  users,
  courses,
  enrollments,
  revenue,
  engagement,
  webinars,
  newsletter,
  loading = false,
  groupBy = "month",
  onChangeGroupBy,
}) {
  const currency = revenue?.currency || overview?.revenue?.currency || "XOF"

  return (
    <div className="space-y-10">
      {/* Header filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#203a8a]">
            Vue d'ensemble de la plateforme
          </h2>
          <p className="text-sm text-gray-500">
            Statistiques globales et tendances
          </p>
        </div>
        {onChangeGroupBy && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Regroupement :</label>
            <select
              value={groupBy}
              onChange={(e) => onChangeGroupBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a]"
            >
              <option value="day">Jour</option>
              <option value="week">Semaine</option>
              <option value="month">Mois</option>
              <option value="year">Année</option>
            </select>
          </div>
        )}
      </div>

      {/* ================= OVERVIEW KPI CARDS ================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">KPIs globaux</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Utilisateurs"
            value={overview?.users?.total}
            subtitle={`${overview?.users?.active ?? 0} actifs · ${overview?.users?.verified ?? 0} vérifiés`}
            icon={Users}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
            growthRate={overview?.users?.growthRate}
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Cours"
            value={overview?.courses?.total}
            subtitle={`${overview?.courses?.published ?? 0} publiés · ${overview?.courses?.draft ?? 0} brouillons`}
            icon={BookOpen}
            iconColor="text-orange-600"
            iconBgColor="bg-orange-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Inscriptions"
            value={overview?.enrollments?.total}
            subtitle={`${overview?.enrollments?.active ?? 0} actives · ${overview?.enrollments?.completionRate ?? 0}% complétion`}
            icon={UserCheck}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Revenus"
            value={overview?.revenue?.total}
            subtitle={`${overview?.revenue?.successfulPayments ?? 0} paiements réussis`}
            icon={DollarSign}
            iconColor="text-purple-600"
            iconBgColor="bg-purple-100"
            growthRate={overview?.revenue?.growthRate}
            formatter={(v) => formatMoney(v, overview?.revenue?.currency)}
            loading={loading}
          />
          <StatCard
            title="Webinaires"
            value={overview?.webinars?.total}
            subtitle={`${overview?.webinars?.upcoming ?? 0} à venir`}
            icon={Video}
            iconColor="text-pink-600"
            iconBgColor="bg-pink-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Newsletter"
            value={overview?.newsletter?.total}
            subtitle={`${overview?.newsletter?.active ?? 0} actifs`}
            icon={Mail}
            iconColor="text-cyan-600"
            iconBgColor="bg-cyan-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Engagement"
            value={overview?.engagement?.comments}
            subtitle={`${overview?.engagement?.reviews ?? 0} avis · ${overview?.engagement?.averageRating ?? 0}★ · ${overview?.engagement?.certificates ?? 0} certificats`}
            icon={MessageCircle}
            iconColor="text-indigo-600"
            iconBgColor="bg-indigo-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Messages contact"
            value={overview?.contact?.total}
            subtitle={`${overview?.contact?.unread ?? 0} non lus`}
            icon={Mail}
            iconColor="text-red-600"
            iconBgColor="bg-red-100"
            formatter={formatNumber}
            loading={loading}
          />
        </div>
      </section>

      {/* ================= USERS STATS ================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Utilisateurs</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LineChart
            title="Inscriptions dans le temps"
            data={users?.registrations || []}
            valueKey="count"
            color="#203a8a"
            fillColor="rgba(31, 58, 138, 0.1)"
            loading={loading}
          />
          <DonutChart
            title="Répartition par rôle"
            data={users?.byRole || []}
            loading={loading}
            valueFormatter={formatNumber}
          />
          <DonutChart
            title="Répartition par statut"
            data={users?.byStatus || []}
            colors={["#10b981", "#ef4444", "#f59e0b", "#6b7280"]}
            loading={loading}
            valueFormatter={formatNumber}
          />
          <DonutChart
            title="Répartition par genre"
            data={users?.byGender || []}
            colors={["#3b82f6", "#ec4899", "#8b5cf6"]}
            loading={loading}
            valueFormatter={formatNumber}
          />
          <BarChart
            title="Top pays"
            data={users?.topCountries || []}
            loading={loading}
            valueFormatter={formatNumber}
          />
          <DonutChart
            title="Vérification email"
            data={users?.verification || []}
            colors={["#10b981", "#f59e0b"]}
            loading={loading}
            valueFormatter={formatNumber}
          />
        </div>
      </section>

      {/* ================= COURSES STATS ================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Cours</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LineChart
            title="Créations de cours"
            data={courses?.creations || []}
            valueKey="count"
            color="#f59e0b"
            fillColor="rgba(245, 158, 11, 0.1)"
            loading={loading}
          />
          <DonutChart
            title="Répartition par statut"
            data={courses?.byStatus || []}
            colors={["#10b981", "#f59e0b", "#6b7280"]}
            loading={loading}
            valueFormatter={formatNumber}
          />
          <DonutChart
            title="Répartition par niveau"
            data={courses?.byLevel || []}
            colors={["#10b981", "#3b82f6", "#8b5cf6"]}
            loading={loading}
            valueFormatter={formatNumber}
          />
          <BarChart
            title="Répartition par catégorie"
            data={courses?.byCategory || []}
            loading={loading}
            valueFormatter={formatNumber}
          />
          <TopList
            title="Top cours par inscriptions"
            items={(courses?.topByEnrollments || []).map((c) => ({
              id: c.id,
              label: c.title,
              value: c.students,
              sublabel: c.rating ? `★ ${c.rating}` : null,
            }))}
            loading={loading}
            valueFormatter={formatNumber}
            valueSuffix=" étudiants"
          />
          <TopList
            title="Top cours par note"
            items={(courses?.topByRating || []).map((c) => ({
              id: c.id,
              label: c.title,
              value: c.rating,
              sublabel: `${c.totalRatings ?? 0} avis`,
            }))}
            loading={loading}
            valueSuffix=" ★"
          />
          <TopList
            title="Top cours par revenus"
            items={(courses?.topByRevenue || []).map((c) => ({
              id: c.id,
              label: c.title,
              value: c.revenue,
              sublabel: `${c.sales ?? 0} ventes`,
            }))}
            loading={loading}
            valueFormatter={(v) => formatMoney(v, currency)}
          />
        </div>
      </section>

      {/* ================= ENROLLMENTS STATS ================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Inscriptions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total inscriptions"
            value={enrollments?.total}
            icon={UserCheck}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Complétées"
            value={enrollments?.completed}
            icon={CheckCircle}
            iconColor="text-emerald-600"
            iconBgColor="bg-emerald-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Taux de complétion"
            value={enrollments?.completionRate}
            icon={TrendingUp}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
            formatter={(v) => `${v ?? 0}%`}
            loading={loading}
          />
          <StatCard
            title="Progression moyenne"
            value={enrollments?.averageProgress}
            icon={Clock}
            iconColor="text-purple-600"
            iconBgColor="bg-purple-100"
            formatter={(v) => `${v ?? 0}%`}
            loading={loading}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LineChart
            title="Inscriptions dans le temps"
            data={enrollments?.series || []}
            valueKey="count"
            color="#10b981"
            fillColor="rgba(16, 185, 129, 0.1)"
            loading={loading}
          />
          <DonutChart
            title="Répartition par statut"
            data={enrollments?.byStatus || []}
            colors={["#3b82f6", "#f59e0b", "#10b981", "#ef4444"]}
            loading={loading}
            valueFormatter={formatNumber}
          />
        </div>
      </section>

      {/* ================= REVENUE STATS ================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Revenus</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard
            title="Total"
            value={revenue?.totals?.total}
            icon={DollarSign}
            iconColor="text-purple-600"
            iconBgColor="bg-purple-100"
            formatter={(v) => formatMoney(v, currency)}
            loading={loading}
          />
          <StatCard
            title="Moyenne par paiement"
            value={revenue?.totals?.average}
            icon={TrendingUp}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
            formatter={(v) => formatMoney(Math.round(v ?? 0), currency)}
            loading={loading}
          />
          <StatCard
            title="Paiements réussis"
            value={revenue?.totals?.successfulPayments}
            icon={CheckCircle}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            formatter={formatNumber}
            loading={loading}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LineChart
            title="Évolution des revenus"
            data={revenue?.series || []}
            valueKey="revenue"
            color="#8b5cf6"
            fillColor="rgba(139, 92, 246, 0.1)"
            loading={loading}
            valueFormatter={(v) => formatMoney(v, currency)}
          />
          <BarChart
            title="Revenus par méthode de paiement"
            data={(revenue?.byMethod || []).map((m) => ({
              label: m.label,
              value: m.revenue,
            }))}
            loading={loading}
            valueFormatter={(v) => formatMoney(v, currency)}
          />
          <DonutChart
            title="Paiements par statut"
            data={(revenue?.byStatus || []).map((s) => ({
              label: s.label,
              value: s.count,
            }))}
            colors={["#10b981", "#f59e0b", "#ef4444"]}
            loading={loading}
            valueFormatter={formatNumber}
          />
          <BarChart
            title="Revenus par type"
            data={(revenue?.byType || []).map((t) => ({
              label: t.label,
              value: t.revenue,
            }))}
            loading={loading}
            valueFormatter={(v) => formatMoney(v, currency)}
          />
        </div>
      </section>

      {/* ================= ENGAGEMENT STATS ================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Engagement</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard
            title="Tentatives quiz"
            value={engagement?.quiz?.totalAttempts}
            icon={Award}
            iconColor="text-indigo-600"
            iconBgColor="bg-indigo-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Quiz réussis"
            value={engagement?.quiz?.passed}
            icon={CheckCircle}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Taux de réussite"
            value={engagement?.quiz?.passRate}
            icon={TrendingUp}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
            formatter={(v) => `${v ?? 0}%`}
            loading={loading}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LineChart
            title="Commentaires dans le temps"
            data={engagement?.commentsSeries || []}
            valueKey="count"
            color="#3b82f6"
            fillColor="rgba(59, 130, 246, 0.1)"
            loading={loading}
          />
          <LineChart
            title="Avis dans le temps"
            data={engagement?.reviewsSeries || []}
            valueKey="count"
            color="#f59e0b"
            fillColor="rgba(245, 158, 11, 0.1)"
            loading={loading}
          />
          <BarChart
            title="Distribution des notes"
            data={(engagement?.ratingDistribution || []).map((r) => ({
              label: `${r.rating} ★`,
              value: r.value,
            }))}
            colors={["#ef4444", "#f97316", "#f59e0b", "#84cc16", "#10b981"]}
            loading={loading}
            valueFormatter={formatNumber}
          />
          <TopList
            title="Top cours commentés"
            items={(engagement?.topCommentedCourses || []).map((c) => ({
              id: c.id,
              label: c.title,
              value: c.comments,
            }))}
            loading={loading}
            valueFormatter={formatNumber}
            valueSuffix=" commentaires"
          />
        </div>
      </section>

      {/* ================= WEBINARS STATS ================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Webinaires</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard
            title="Total inscriptions"
            value={webinars?.registrations?.total}
            icon={Users}
            iconColor="text-pink-600"
            iconBgColor="bg-pink-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Participations"
            value={webinars?.registrations?.attended}
            icon={UserCheck}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Taux de participation"
            value={webinars?.registrations?.attendanceRate}
            icon={TrendingUp}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
            formatter={(v) => `${v ?? 0}%`}
            loading={loading}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LineChart
            title="Webinaires créés dans le temps"
            data={webinars?.series || []}
            valueKey="count"
            color="#ec4899"
            fillColor="rgba(236, 72, 153, 0.1)"
            loading={loading}
          />
          <DonutChart
            title="Répartition par statut"
            data={webinars?.byStatus || []}
            colors={["#3b82f6", "#10b981", "#ef4444", "#6b7280"]}
            loading={loading}
            valueFormatter={formatNumber}
          />
          <div className="lg:col-span-2">
            <TopList
              title="Top webinaires par inscriptions"
              items={(webinars?.topByRegistrations || []).map((w) => ({
                id: w.id,
                label: w.title,
                value: w.registrations,
                sublabel: `${w.status} · ${new Date(w.date).toLocaleDateString("fr-FR")}`,
              }))}
              loading={loading}
              valueFormatter={formatNumber}
              valueSuffix=" inscrits"
            />
          </div>
        </div>
      </section>

      {/* ================= NEWSLETTER STATS ================= */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Newsletter</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Abonnés"
            value={newsletter?.subscribers?.total}
            subtitle={`${newsletter?.subscribers?.active ?? 0} actifs`}
            icon={Mail}
            iconColor="text-cyan-600"
            iconBgColor="bg-cyan-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Campagnes envoyées"
            value={newsletter?.campaigns?.total}
            subtitle={`${newsletter?.campaigns?.totalRecipients ?? 0} destinataires`}
            icon={Mail}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
            formatter={formatNumber}
            loading={loading}
          />
          <StatCard
            title="Taux d'ouverture"
            value={newsletter?.campaigns?.openRate}
            subtitle={`${newsletter?.campaigns?.opened ?? 0} ouvertures`}
            icon={TrendingUp}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
            formatter={(v) => `${v ?? 0}%`}
            loading={loading}
          />
          <StatCard
            title="Taux de clic"
            value={newsletter?.campaigns?.clickRate}
            subtitle={`${newsletter?.campaigns?.clicked ?? 0} clics`}
            icon={Star}
            iconColor="text-purple-600"
            iconBgColor="bg-purple-100"
            formatter={(v) => `${v ?? 0}%`}
            loading={loading}
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <LineChart
            title="Évolution des abonnés"
            data={newsletter?.subscribersSeries || []}
            valueKey="count"
            color="#06b6d4"
            fillColor="rgba(6, 182, 212, 0.1)"
            loading={loading}
          />
          <DonutChart
            title="Campagnes par statut"
            data={newsletter?.byStatus || []}
            colors={["#10b981", "#6b7280", "#3b82f6", "#f59e0b"]}
            loading={loading}
            valueFormatter={formatNumber}
          />
        </div>
      </section>
    </div>
  )
}
